// Helpers for admin impersonation. We swap the admin's `token`/`user` in
// localStorage for the target's, but stash the originals under
// `impersonation_*` so we can restore them on Exit.
//
// Convention:
//   - During impersonation, `localStorage.token` = target's JWT (which
//     carries an `impersonator_id` claim — visible to the backend audit log
//     when actions are taken).
//   - `localStorage.impersonation_admin_token` = the admin's original JWT.
//   - `localStorage.impersonation_admin_user`  = the admin's User JSON.
//   - `localStorage.impersonation_target_email` = banner label.
//   - `localStorage.impersonation_ends_at`     = ISO datetime to auto-exit.

const STORAGE_KEYS = {
  ADMIN_TOKEN: 'impersonation_admin_token',
  ADMIN_USER: 'impersonation_admin_user',
  TARGET_EMAIL: 'impersonation_target_email',
  ENDS_AT: 'impersonation_ends_at',
};

export const isImpersonating = () => {
  return !!localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
};

export const getImpersonationTargetEmail = () => {
  return localStorage.getItem(STORAGE_KEYS.TARGET_EMAIL) || '';
};

export const getImpersonationEndsAt = () => {
  const value = localStorage.getItem(STORAGE_KEYS.ENDS_AT);
  if (!value) return null;
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
  } catch {
    return null;
  }
};

// Start impersonation: save admin context, install target context, reload.
export const startImpersonation = ({ adminToken, adminUser, targetToken, targetUser, ttlHours = 1 }) => {
  if (!adminToken || !targetToken || !targetUser) return;
  localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, adminToken);
  localStorage.setItem(STORAGE_KEYS.ADMIN_USER, JSON.stringify(adminUser || {}));
  localStorage.setItem(STORAGE_KEYS.TARGET_EMAIL, targetUser.email || targetUser.name || 'user');
  const endsAt = new Date(Date.now() + ttlHours * 60 * 60 * 1000);
  localStorage.setItem(STORAGE_KEYS.ENDS_AT, endsAt.toISOString());
  localStorage.setItem('token', targetToken);
  localStorage.setItem('user', JSON.stringify(targetUser));
};

// Stop impersonation: restore admin context, clear flags, return admin token.
export const stopImpersonation = () => {
  const adminToken = localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
  const adminUserRaw = localStorage.getItem(STORAGE_KEYS.ADMIN_USER);
  if (adminToken) {
    localStorage.setItem('token', adminToken);
    if (adminUserRaw) {
      try {
        localStorage.setItem('user', adminUserRaw);
      } catch {
        // leave user as-is
      }
    }
  }
  localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ADMIN_USER);
  localStorage.removeItem(STORAGE_KEYS.TARGET_EMAIL);
  localStorage.removeItem(STORAGE_KEYS.ENDS_AT);
  return adminToken;
};
